import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
    Box,
    Button,
    TextField,
    Typography,
    LinearProgress,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Paper,
    CircularProgress
} from "@material-ui/core";
import { CheckCircle, ArrowBack, ArrowForward } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { getStoredUTMParams } from "../../utils/utmCapture";
import { formatPhoneForBrazil, formatPhoneDisplay, isValidPhone } from "../../utils/phoneFormatter";

const QuizForm = ({ slug, landingPageId, questions, primaryColor, logo, footerText }) => {
    const history = useHistory();
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [phoneDisplay, setPhoneDisplay] = useState("");

    // Injetar estilos agressivos para forçar o scroll na página do quiz
    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'quiz-scroll-fix';
        style.innerHTML = `
            html, body, #root {
                overflow: auto !important;
                height: auto !important;
                min-height: 100vh !important;
            }
            .app-wrapper, .page-layout, .gxon-wrapper {
                overflow: visible !important;
                height: auto !important;
                min-height: 100vh !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            const el = document.getElementById('quiz-scroll-fix');
            if (el) el.remove();
        };
    }, []);

    // Carregar respostas salvas do sessionStorage
    useEffect(() => {
        const savedAnswers = sessionStorage.getItem(`quiz_answers_${landingPageId} `);
        if (savedAnswers) {
            try {
                setAnswers(JSON.parse(savedAnswers));
            } catch (e) {
                console.error("Error loading saved answers:", e);
            }
        }
    }, [landingPageId]);

    // Salvar respostas no sessionStorage sempre que mudar
    useEffect(() => {
        if (Object.keys(answers).length > 0) {
            sessionStorage.setItem(`quiz_answers_${landingPageId} `, JSON.stringify(answers));
        }
    }, [answers, landingPageId]);

    const currentQuestion = questions[currentStep];
    const totalSteps = questions.length;
    const progress = ((currentStep + 1) / totalSteps) * 100;

    const handleAnswer = (questionId, value) => {
        setAnswers({
            ...answers,
            [questionId]: value
        });
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value;
        setPhoneDisplay(value);

        // Atualizar resposta com valor formatado
        if (isValidPhone(value)) {
            const formatted = formatPhoneForBrazil(value);
            handleAnswer(currentQuestion.id, formatted);
        } else {
            handleAnswer(currentQuestion.id, value);
        }
    };

    const canProceed = () => {
        const answer = answers[currentQuestion.id];

        if (currentQuestion.required) {
            if (currentQuestion.type === "phone") {
                return answer && isValidPhone(phoneDisplay);
            }
            return answer && answer.trim().length > 0;
        }

        return true;
    };

    const handleNext = () => {
        if (!canProceed()) {
            toast.warning("Por favor, responda a pergunta para continuar");
            return;
        }

        if (currentStep < totalSteps - 1) {
            setCurrentStep(currentStep + 1);

            // Se próxima pergunta é telefone, carregar valor salvo
            if (questions[currentStep + 1].type === "phone" && answers[questions[currentStep + 1].id]) {
                setPhoneDisplay(formatPhoneDisplay(answers[questions[currentStep + 1].id]));
            }
        } else {
            handleSubmit();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);

            // Se pergunta anterior é telefone, carregar valor salvo
            if (questions[currentStep - 1].type === "phone" && answers[questions[currentStep - 1].id]) {
                setPhoneDisplay(formatPhoneDisplay(answers[questions[currentStep - 1].id]));
            }
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // Recuperar parâmetros UTM
            const utmParams = getStoredUTMParams() || {};

            // Encontrar resposta do telefone
            const phoneQuestion = questions.find(q => q.type === "phone");
            const phone = answers[phoneQuestion?.id];

            if (!phone) {
                toast.error("Telefone é obrigatório");
                setLoading(false);
                return;
            }

            // Tentar extrair nome das respostas do quiz
            let name = null;
            const nameQuestion = questions.find(q =>
                q.question.toLowerCase().includes("nome") && q.type === "text"
            );
            if (nameQuestion && answers[nameQuestion.id]) {
                name = answers[nameQuestion.id];
            }

            // Tentar extrair email das respostas do quiz
            let email = null;
            const emailQuestion = questions.find(q =>
                q.question.toLowerCase().includes("email") || q.question.toLowerCase().includes("e-mail")
            );
            if (emailQuestion && answers[emailQuestion.id]) {
                email = answers[emailQuestion.id];
            }

            // Preparar respostas com perguntas para exibição
            const answersWithQuestions = {};
            questions.forEach(q => {
                if (answers[q.id]) {
                    answersWithQuestions[q.id] = {
                        question: q.question,
                        answer: answers[q.id]
                    };
                }
            });

            // Enviar lead com respostas do quiz
            await api.post("/campaign-leads", {
                landingPageId,
                phone,
                name,
                email,
                quizAnswers: answersWithQuestions,
                // UTM parameters in snake_case
                utm_source: utmParams.utmSource || utmParams.utm_source,
                utm_medium: utmParams.utmMedium || utmParams.utm_medium,
                utm_campaign: utmParams.utmCampaign || utmParams.utm_campaign,
                utm_content: utmParams.utmContent || utmParams.utm_content,
                utm_term: utmParams.utmTerm || utmParams.utm_term,
                gclid: utmParams.gclid,
                fbclid: utmParams.fbclid,
                referrer: document.referrer || undefined,
                landing_url: window.location.href
            });

            setSuccess(true);
            toast.success("Obrigado! Entraremos em contato em breve.");

            // Limpar respostas salvas
            sessionStorage.removeItem(`quiz_answers_${landingPageId}`);
        } catch (error) {
            console.error("Error submitting quiz:", error);
            toast.error("Erro ao enviar seus dados. Tente novamente.");
        } finally {
            setLoading(false);
        }
    };

    // Redirect após 3 segundos quando success
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                // Redirecionar para a landing page
                window.location.href = `/lp/${slug}`;
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [success, slug]);

    if (success) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="80vh"
                textAlign="center"
                p={4}
            >
                {logo && (
                    <img src={logo} alt="Logo" style={{ maxWidth: 200, marginBottom: 30 }} />
                )}
                <CheckCircle style={{ fontSize: 100, color: "#4caf50", marginBottom: 20 }} />
                <Typography variant="h4" gutterBottom style={{ fontWeight: 700 }}>
                    Obrigado!
                </Typography>
                <Typography variant="h6" color="textSecondary">
                    Recebemos suas informações e entraremos em contato em breve.
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 20 }}>
                    Redirecionando em 3 segundos...
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="auto"
            minHeight="100vh"
            style={{ backgroundColor: "#f5f5f5" }}
        >
            {/* Logo */}
            {logo && (
                <Box textAlign="center" py={3} style={{ backgroundColor: "white" }}>
                    <img src={logo} alt="Logo" style={{ maxHeight: 60, maxWidth: 200 }} />
                </Box>
            )}

            {/* Progress Bar */}
            <Box px={2} py={2} style={{ backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                        Pergunta {currentStep + 1} de {totalSteps}
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 600, color: primaryColor }}>
                        {Math.round(progress)}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    style={{ height: 8, borderRadius: 4 }}
                />
            </Box>

            {/* Question Content */}
            <Box flex={1} display="flex" justifyContent="center" p={3}>
                <Paper elevation={3} style={{ maxWidth: 600, width: "100%", padding: 40, borderRadius: 15, margin: "40px auto" }}>
                    <Typography variant="h5" gutterBottom style={{ fontWeight: 600, marginBottom: 30 }}>
                        {currentQuestion.question}
                        {currentQuestion.required && <span style={{ color: "red" }}> *</span>}
                    </Typography>

                    {/* Text Input */}
                    {currentQuestion.type === "text" && (
                        <TextField
                            fullWidth
                            variant="outlined"
                            value={answers[currentQuestion.id] || ""}
                            onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                            placeholder="Digite sua resposta..."
                            autoFocus
                        />
                    )}

                    {/* Phone Input */}
                    {currentQuestion.type === "phone" && (
                        <TextField
                            fullWidth
                            variant="outlined"
                            value={phoneDisplay}
                            onChange={handlePhoneChange}
                            placeholder="(00) 00000-0000"
                            autoFocus
                            helperText="Digite seu telefone com DDD"
                        />
                    )}

                    {/* Multiple Choice */}
                    {currentQuestion.type === "options" && (
                        <FormControl component="fieldset" fullWidth>
                            <RadioGroup
                                value={answers[currentQuestion.id] || ""}
                                onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                            >
                                {currentQuestion.options?.map((option, index) => (
                                    <FormControlLabel
                                        key={index}
                                        value={option}
                                        control={<Radio color="primary" />}
                                        label={option}
                                        style={{
                                            marginBottom: 10,
                                            padding: 15,
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 8,
                                            backgroundColor: answers[currentQuestion.id] === option ? "#f0f7ff" : "white"
                                        }}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    )}

                    {/* Navigation Buttons */}
                    <Box display="flex" justifyContent="space-between" mt={4}>
                        <Button
                            variant="outlined"
                            onClick={handleBack}
                            disabled={currentStep === 0 || loading}
                            startIcon={<ArrowBack />}
                        >
                            Voltar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!canProceed() || loading}
                            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
                            style={{
                                backgroundColor: canProceed() ? primaryColor : "#ccc",
                                color: "white",
                                minWidth: 150
                            }}
                        >
                            {currentStep === totalSteps - 1 ? "Enviar" : "Próximo"}
                        </Button>
                    </Box>
                </Paper>
            </Box>

            {/* Footer */}
            {footerText && (
                <Box textAlign="center" py={2} style={{ backgroundColor: "#333", color: "white" }}>
                    <Typography variant="body2">{footerText}</Typography>
                </Box>
            )}
        </Box>
    );
};

export default QuizForm;
